import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { brand } from "@/data/site/brand";
import { servicesMenu, resourcesMenu, aboutMenu } from "@/data/site/navigation";
import ctrLogoAsset from "@/assets/ctr-logo.webp.asset.json";
import { SocialLinks } from "@/components/site/SocialLinks";

export const SiteFooter = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-gradient-navy text-white">
      <div className="h-1.5 w-full bg-gradient-to-r from-navy via-gold to-navy" aria-hidden="true" />
      <div className="container mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2">
          <div className="mb-4 bg-white rounded p-2 inline-block">
            <img src={ctrLogoAsset.url} alt={brand.legalName} className="h-14 w-auto"  width={250} height={150} />
          </div>
          <p className="text-sm text-white/70 mb-4 max-w-sm">
            CPA-led IRS tax resolution. We help individuals and businesses settle back taxes,
            release wage garnishments and bank levies, defend audits, and negotiate Offers in
            Compromise — nationwide.
          </p>
          <ul className="space-y-2 text-sm text-white/80">
            <li className="flex items-start gap-2">
              <Phone className="w-4 h-4 mt-0.5 text-gold" />
              <a href={`tel:${brand.phoneTel}`} className="hover:text-gold">{brand.phone}</a>
            </li>
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-0.5 text-gold" />
              <a href={`mailto:${brand.email}`} className="hover:text-gold break-all">{brand.email}</a>
            </li>
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-gold" />
              <span>{brand.address.full}</span>
            </li>
          </ul>
          <div className="mt-5">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold mb-2">Follow Us</div>
            <SocialLinks variant="footer" />
          </div>
        </div>

        <FooterCol title="Services">
          {servicesMenu.map((c) => (
            <li key={c.title}>
              <Link to={c.href ?? "/services"} className="text-sm text-white/70 hover:text-gold">
                {c.title}
              </Link>
            </li>
          ))}
          <li className="pt-2">
            <Link to="/services" aria-label="View all tax resolution services" className="text-sm font-semibold text-gold hover:underline">
              All services →
            </Link>
          </li>
        </FooterCol>

        <FooterCol title="Resources">
          {resourcesMenu[0].items.map((i) => (
            <li key={i.href}>
              <Link to={i.href} className="text-sm text-white/70 hover:text-gold">
                {i.name}
              </Link>
            </li>
          ))}
        </FooterCol>

        <FooterCol title="Firm">
          {aboutMenu[0].items.map((i) => (
            <li key={i.href}>
              <Link to={i.href} className="text-sm text-white/70 hover:text-gold">
                {i.name}
              </Link>
            </li>
          ))}
          <li>
            <Link to="/service-areas" className="text-sm text-white/70 hover:text-gold">
              Service Areas
            </Link>
          </li>
        </FooterCol>
      </div>

      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/60">
          <p>© {year} {brand.legalName}. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 justify-center">
            <Link to="/editorial-standards" className="hover:text-gold">Editorial Standards</Link>
            <Link to="/privacy-policy" className="hover:text-gold">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gold">Terms of Use</Link>
            <Link to="/disclaimer" className="hover:text-gold">Disclaimer</Link>
          </div>
        </div>
        <div className="container mx-auto px-4 pb-6 text-[11px] text-white/75 leading-relaxed">
          Comprehensive Tax Resolution LLC is a CPA firm. We are not a law firm and do not provide
          legal advice. CPAs are federally authorized tax practitioners and may represent taxpayers
          before the IRS in all matters. Results vary based on each client's facts and circumstances.
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div>
    <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-gold mb-4">
      {title}
    </h2>
    <ul className="space-y-2">{children}</ul>
  </div>
);
